import { useEffect, useRef, useState } from "react";

/**
 * useStaggerReveal — returns a `{ ref, visible }` pair that flips `visible`
 * to true once the target element enters the viewport. Combine with the
 * `.luxstay-scope-card` / `.is-visible` CSS classes (see index.css) and an
 * inline `transitionDelay` per index to produce a staggered fade+slide-up
 * entrance. Honors `prefers-reduced-motion` automatically.
 *
 * Usage:
 *   const { ref, visible } = useStaggerReveal();
 *   <section ref={ref}>
 *     {items.map((it, i) => (
 *       <div className={`luxstay-scope-card ${visible ? "is-visible" : ""}`}
 *            style={{ transitionDelay: visible ? `${i * 80}ms` : "0ms" }} />
 *     ))}
 *   </section>
 */
export default function useStaggerReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}
